import { ogImageMeta } from "@/lib/og-images";
import { createFileRoute, Link } from "@tanstack/react-router";
import logo from "@/assets/thevibingco_logo.svg.asset.json";
import { MagneticButton } from "@/components/magnetic-button";
import { TalkToJonButton } from "@/components/talk-to-jon";
import { SiteFooter } from "@/components/site-footer";
import { PricingPlans } from "@/components/pricing-plans";

export const FAQS = [
  {
    q: "What does it mean to rescue a vibe-coded app?",
    a: "You shipped fast with AI tools and now something is broken — auth, payments, performance, security, or the codebase itself. We diagnose what's actually wrong, fix the urgent thing first, then leave you with a project you can keep building on without fear.",
  },
  {
    q: "Can you fix a Lovable project that's totally stuck?",
    a: "Yes. Most rescues start the same way: a screen-share, a list of the three scariest bugs, and a plan to ship a fix within the first 48 hours. Even when the project is past saving, we'll tell you that on day one — not after a month of invoices.",
  },
  {
    q: "Is my vibe-coded app safe to launch?",
    a: "Maybe. We run a focused audit: row-level security, exposed keys, auth gaps, rate-limiting, input validation, and the prompt-injection holes vibe-coded apps usually ship with. You get a one-page report and a fix list, prioritised.",
  },
  {
    q: "How much does a vibe coding rescue cost?",
    a: "Most rescues fit a fixed-scope week. We quote the week, you approve, we ship. If we find the project needs more, we tell you on day two — not at the invoice.",
  },
];

export const Route = createFileRoute("/services/vibe-coding-rescue")({
  head: () => ({
    meta: [
      { title: "Fix My Vibe-Coded App · Vibe Coding Rescue | the Vibing Company®" },
      {
        name: "description",
        content:
          "Vibe coding rescue for Lovable, Cursor and Claude Code projects. We fix broken auth, leaking backends and stalled MVPs in a week. Fixed scope.",
      },
      { property: "og:title", content: "Fix My Vibe-Coded App · Vibe Coding Rescue" },
      ...ogImageMeta("services-vibe-coding-rescue", "Share card: fix my vibe-coded app"),
      {
        property: "og:description",
        content:
          "We fix broken vibe-coded apps in a week. Auth, security, performance, the works. Fixed scope, no retainers.",
      },
      { property: "og:url", content: "https://vibing.inc/services/vibe-coding-rescue" },
    ],
    links: [{ rel: "canonical", href: "https://vibing.inc/services/vibe-coding-rescue" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Vibe coding rescue",
          serviceType: "Fix vibe coded app · rescue stalled Lovable project",
          provider: { "@type": "Organization", name: "the Vibing Company", url: "https://vibing.inc/" },
          areaServed: "Worldwide",
          url: "https://vibing.inc/services/vibe-coding-rescue",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: Rescue,
});

function Rescue() {
  return (
    <div className="min-h-dvh bg-background text-foreground">
      <main id="main" tabIndex={-1}>
      <Hero />
      <Symptoms />
      <Process />
      <FAQ />
      <PricingPlans heading="After the rescue" />
      <CTA />
      </main>
      <SiteFooter />
    </div>
  );
}


function Hero() {
  return (
    <section aria-labelledby="rs-h" className="relative mx-auto max-w-[1400px] px-6 md:px-10 pt-[clamp(9rem,18vh,13rem)] pb-16 md:pb-24">
      <h1 id="rs-h" className="mt-8 font-display tracking-[-0.035em] leading-[1.02] max-w-[20ch]" style={{ fontSize: "clamp(2rem, 4.4vw, 3.5rem)" }}>
        Fix my vibe-coded app.
      </h1>
      <p className="mt-10 max-w-[60ch] text-xl md:text-2xl leading-snug text-foreground">
        You shipped a Lovable project in a weekend and now something is on fire. Broken auth, leaking backend, payments that don't fire, design that's collapsing. We diagnose it, shape the right intervention, and fix it.
      </p>
      <div className="mt-10 md:mt-14 flex flex-wrap gap-3">
        <TalkToJonButton variant="dark" immersive />
        <MagneticButton href="/services/lovable-development" variant="outline-dark">
          Or build something new
        </MagneticButton>
      </div>
    </section>
  );
}

const SYMPTOMS = [
  { k: "01", t: "Auth is leaking", b: "Anyone with a browser can see another user's data. Classic RLS-not-enabled symptom. We close the holes and lock the schema." },
  { k: "02", t: "Payments don't reconcile", b: "Stripe webhooks fire into the void. Orders go through, the database doesn't know. We wire the loop properly." },
  { k: "03", t: "The page takes 12 seconds", b: "Bundle is enormous, every component fetches the same data twice. We rebuild the hot path so it loads in under one." },
  { k: "04", t: "Every prompt breaks something else", b: "The codebase has 40 unused files and three competing patterns. We refactor it into something Lovable can actually keep working on." },
];

function Symptoms() {
  return (
    <section aria-labelledby="sx-h" className="relative border-t border-border/60 mx-auto max-w-[1400px] px-6 md:px-10 py-20 md:py-32">
      <h2 id="sx-h" className="fluid-h2 font-display max-w-[18ch]">If any of this sounds familiar, you're not alone.</h2>
      <ul className="mt-14 grid md:grid-cols-2 gap-5">
        {SYMPTOMS.map((s) => (
          <li key={s.k} className="bg-background p-8 md:p-12">
            <div className="flex items-baseline gap-5">
              <span className="font-display text-electric leading-none" style={{ fontSize: "clamp(2.5rem, 4vw, 4rem)" }}>{s.k}</span>
              <h3 className="font-display tracking-[-0.03em] leading-[1.02]" style={{ fontSize: "clamp(1.5rem, 2.2vw, 2.25rem)" }}>{s.t}</h3>
            </div>
            <p className="mt-6 text-base md:text-lg text-muted-foreground leading-snug max-w-[44ch]">{s.b}</p>
          </li>
        ))}
      </ul>
    </section>
  );
}

const STEPS = [
  { k: "Hour 1", v: "Screen-share. You show us the fire. We tell you whether it's a one-day fix or a one-week fix." },
  { k: "Day 1", v: "Written triage in your inbox. The three things we'll ship first, in order, with risks called out." },
  { k: "Day 2-4", v: "We ship in your workspace. Daily diffs. You can stop us at any point." },
  { k: "Day 5", v: "Demo, handover, a short doc on what changed and why. You go back to building." },
];

function Process() {
  return (
    <section aria-labelledby="proc-h" className="relative border-t border-border/60 bg-secondary/40">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-20 md:py-32 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-4">
          <h2 id="proc-h" className="fluid-h2 font-display max-w-[14ch]">Five days, fixed scope.</h2>
        </div>
        <ul className="md:col-span-7 md:col-start-6 border-t border-foreground/20">
          {STEPS.map((s) => (
            <li key={s.k} className="grid grid-cols-[100px_1fr] md:grid-cols-[140px_1fr] gap-6 items-baseline py-6 border-b border-foreground/20">
              <span className="font-display tracking-[-0.03em]" style={{ fontSize: "clamp(1.5rem, 2.2vw, 2.25rem)" }}>{s.k}</span>
              <span className="text-base md:text-lg leading-snug">{s.v}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function FAQ() {
  return (
    <section aria-labelledby="faq-h" className="relative border-t border-border/60 mx-auto max-w-[1400px] px-6 md:px-10 py-20 md:py-32">
      <h2 id="faq-h" className="fluid-h2 font-display max-w-[18ch]">Rescuing a vibe-coded app, answered.</h2>
      <dl className="mt-14 border-t border-border">
        {FAQS.map((f) => (
          <div key={f.q} className="grid md:grid-cols-12 gap-6 py-8 border-b border-border">
            <dt className="md:col-span-5 font-display text-xl md:text-2xl tracking-[-0.02em] leading-snug">{f.q}</dt>
            <dd className="md:col-span-6 md:col-start-7 text-base md:text-lg text-muted-foreground leading-relaxed">{f.a}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function CTA() {
  return (
    <section aria-labelledby="cta-h" className="relative border-t border-border/60 bg-foreground text-background">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-24 md:py-40 grid md:grid-cols-12 gap-10 items-end">
        <div className="md:col-span-7">
          <h2 id="cta-h" className="font-display tracking-[-0.035em] leading-[1.02]" style={{ fontSize: "clamp(2rem, 4.4vw, 3.5rem)" }}>
            Send the link.
          </h2>
          <p className="mt-8 text-lg md:text-xl text-background/70 max-w-[52ch] leading-snug">
             Bring the broken project to Jon and talk through the fastest responsible way to fix it.
          </p>
        </div>
        <div className="md:col-span-4 md:col-start-9">
          <a href="https://cal.com/vibinc/30min" target="_blank" rel="noopener noreferrer" className="block group">
            <span className="mt-3 block font-display text-electric-bright break-all" style={{ fontSize: "clamp(1.75rem, 3vw, 2.75rem)" }}>
              Talk to Jon<span aria-hidden className="inline-block ml-2 transition-transform group-hover:translate-x-2">↗</span>
            </span>
          </a>
        </div>
      </div>
    </section>
  );
}
